'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useAppStore } from '@/store/app';
import { useRequireAuth } from '@/lib/useRequireAuth';
import EventPing from '@/components/EventPing';
import { api } from '@/lib/api';
import { getAccessToken } from '@/lib/api';
import ResearchTabs from '@/components/ResearchTabs';

/* ------------------------------ helpers ------------------------------ */

const LABEL_MS: Record<string, number> = {
  '1m': 60_000, '5m': 300_000, '15m': 900_000, '1h': 3600_000, '4h': 4 * 3600_000, '6h': 6 * 3600_000,
  '8h': 8 * 3600_000, '12h': 12 * 3600_000, '24h': 86400_000, '2d': 2 * 86400_000, '3d': 3 * 86400_000,
  '7d': 7 * 86400_000, '14d': 14 * 86400_000, '30d': 30 * 86400_000,
};

/** 观察窗口的周期标签（按语言输出，避免依赖后端中文 label）。 */
function labelOfH(h: number, interval: string, zh: boolean): string {
  const ms = LABEL_MS[interval] ?? 3600_000;
  const total = h * ms;
  if (total % 86400_000 === 0) return zh ? `${total / 86400_000}天` : `${total / 86400_000}d`;
  if (total % 3600_000 === 0) return zh ? `${total / 3600_000}小时` : `${total / 3600_000}h`;
  if (total % 60_000 === 0) return zh ? `${total / 60_000}分钟` : `${total / 60_000}m`;
  return zh ? `${h}根` : `${h} bars`;
}

/** 训练/留出说明：兼容新双语对象与旧中文/英文纯字符串。 */
function splitNoteOf(
  note: string | { zh: string; en: string } | undefined,
  zh: boolean,
  split: number,
): string {
  if (!note) return '';
  if (typeof note === 'object') return zh ? note.zh : note.en;
  const s = note as string;
  if (!zh) {
    if (/训练区间|训练区间/.test(s)) return `Training period (first ${Math.round(split * 100)}%)`;
    if (/留出区间|留出区间/.test(s)) return `Held-out period (last ${Math.round((1 - split) * 100)}%), results must not be used for tuning`;
  }
  return s;
}

/* ------------------------------ types ------------------------------ */

interface RuleRow { id: string; name: string; description: string; ruleVersion: number; updatedAt: string; }

interface StudyUniverse { product: 'spot' | 'perp'; venue?: string; quote?: string; topN?: number; }
interface StudyRequest {
  screenId: string;
  ruleVersion?: number;
  universe: StudyUniverse;
  start: number;
  end: number;
  interval: string;
  horizons: number[];
  benchmark: string;
  samplingMode: 'non_overlapping' | 'all';
  split: number;
}

interface HorizonStats {
  h: number; label: string; valid: number; missing: number; reasons: Record<string, number>;
  median: number | null; mean: number | null; posPct: number | null; negPct: number | null; zeroPct: number | null;
  p5: number | null; p25: number | null; p75: number | null; p95: number | null;
  mfeMedian: number | null; mfeMean: number | null; maeMedian: number | null; maeMean: number | null;
  insufficient: boolean;
}

interface StudyReport {
  title: string; basis: string; config: StudyRequest;
  coverage: { instrumentsTried: number; instrumentsWithData: number; avgRatio: number; gapsTotal: number; survivorshipBias: boolean };
  overview: { instruments: number; events: number; suppressedTotal: number; train: number; heldout: number; excluded: number; samplingMode: string };
  horizons: HorizonStats[];
  insufficient: boolean;
  comparisonRanking: string;
  benchmark: { symbol: string | null; diff: Record<string, number | null> };
  split: { train: { horizons: HorizonStats[]; note: string | { zh: string; en: string } }; heldout: { horizons: HorizonStats[]; note: string | { zh: string; en: string } } };
  events: Array<{ instrumentId: string; symbol: string; triggerTime: string; p0: number; split: string; outcomes: Array<{ h: number; valid: boolean; reason?: string; r: number | null; mfe: number | null; mae: number | null }> }>;
  /** D02：固定 scope 与计数（planned/processed/valid/skipped/failed + bar/event）。 */
  preflight: { preflightId: string; configHash: string; selection: string; asOf: string } | null;
  counts: { plannedInstrumentCount: number; processedInstrumentCount: number; validInstrumentCount: number; skippedInstrumentCount: number; failedInstrumentCount: number; barCount: number; eventCount: number } | null;
  partialReasons: string[];
  elapsedMs: number | null;
}

interface StudyJob {
  id: string; state: string; config: StudyRequest;
  progress: { stage?: string; processed?: number; total?: number; valid?: number; skipped?: number; failed?: number } | null;
  result: StudyReport | null;
  error: { code?: string; message?: string } | null;
  ruleVersion: number; createdAt: string; updatedAt: string;
}

const RUNNING_STATES = ['queued', 'validating', 'running', 'cancel_requested'];
const INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d'];
const HORIZON_OPTIONS = [1, 4, 24];

function utcInput(ms: number): string {
  return new Date(ms).toISOString().slice(0, 16);
}
function parseUtcInput(v: string): number {
  if (!v) return 0;
  const t = Date.parse(v.length === 16 ? `${v}:00Z` : v);
  return isFinite(t) ? t : 0;
}
function fmtTime(ts: string): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}
function fmtPct(v: number | null): string {
  if (v === null || !isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${(v * 100).toFixed(2)}%`;
}
function fmtPP(v: number | null): string {
  if (v === null || !isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}pp`;
}
/** D19：p0/价格有效精度 —— 固定 6 位会掩盖极小币，按量级自适应；极小值用有效数字。 */
function fmtPriceSmart(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return '—';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  if (n >= 0.0001) return Number(n.toFixed(6)).toString();
  return n.toPrecision(4);
}

const STATE_LABEL: Record<string, { zh: string; en: string; color: string }> = {
  queued: { zh: '排队中', en: 'Queued', color: 'text-gf-muted' },
  validating: { zh: '预检中', en: 'Validating', color: 'text-gf-muted' },
  running: { zh: '计算中', en: 'Running', color: 'text-gf-accent' },
  succeeded: { zh: '完成', en: 'Succeeded', color: 'text-emerald-500' },
  partial: { zh: '部分完成', en: 'Partial', color: 'text-amber-500' },
  failed: { zh: '失败', en: 'Failed', color: 'text-red-500' },
  cancel_requested: { zh: '取消中', en: 'Cancelling', color: 'text-amber-500' },
  cancelled: { zh: '已取消', en: 'Cancelled', color: 'text-gf-muted' },
};

/** 历史任务 partialReasons 由后端存为中文句子；英文界面就地翻译（覆盖存量+新增）。 */
function fmtStudyReason(r: string, zh: boolean): string {
  if (zh) return r;
  return r
    .replace(/有效标的\s+(\d+)\/(\d+)/g, (_m, a, b) => `valid instruments ${a}/${b}`)
    .replace(/平均数据覆盖\s+([\d.]+)%\s+低于\s+80%/g, (_m, x) => `avg data coverage ${x}% below 80%`)
    .replace(/跳过\s+(\d+)\s+个标的（历史不足）/g, (_m, n) => `skipped ${n} instruments (insufficient history)`)
    .replace(/失败\s+(\d+)\s+个标的/g, (_m, n) => `failed ${n} instruments`)
    .replace(/现货无资金费率/g, 'spot has no funding rate')
    .replace(/无历史未保存/g, 'not saved in history')
    .replace(/历史未保存/g, 'not saved in history');
}

/* ------------------------------ page ------------------------------ */

/* ------------------------------ D19 样本明细分页 ------------------------------ */

interface SampleEvent {
  instrumentId: string;
  symbol: string;
  triggerTime: string;
  p0: number;
  split: 'train' | 'heldout' | 'excluded';
  outcomes: Array<{ h: number; valid: boolean; reason?: string; r: number | null; mfe: number | null; mae: number | null }>;
}

/** 分页 + 筛选的样本明细（/studies/:id/events）。每次最多 50 条，支持币对/分区/缺失原因过滤。 */
function PaginatedEvents({ taskId, zh, report }: { taskId: string; zh: boolean; report: StudyReport }) {
  const [cursor, setCursor] = useState(0);
  const [symbol, setSymbol] = useState('');
  const [split, setSplit] = useState<'all' | 'train' | 'heldout' | 'excluded'>('all');
  const [invalidOnly, setInvalidOnly] = useState(false);
  const PAGE = 50;

  const q = useQuery({
    queryKey: ['study-events', taskId, cursor, symbol, split, invalidOnly],
    queryFn: () =>
      api.get<{ items: SampleEvent[]; total: number; nextCursor: string | null }>(
        `/studies/${taskId}/events?limit=${PAGE}&cursor=${cursor}${symbol ? `&symbol=${encodeURIComponent(symbol)}` : ''}${split !== 'all' ? `&split=${split}` : ''}${invalidOnly ? '&invalidOnly=1' : ''}`,
      ),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  // 旧版本任务仅截断保存前 100 条样本：若存储事件数 < 运行时事件总数，提示重新运行可获全量。
  const legacyTruncated = report.overview.events > total && !q.isLoading;

  const splitLabel = (s: SampleEvent['split']) =>
    s === 'train' ? (zh ? '训练' : 'Train') : s === 'heldout' ? (zh ? '留出' : 'Held-out') : zh ? '剔除' : 'Excluded';
  const splitTone = (s: SampleEvent['split']) =>
    s === 'train' ? 'bg-emerald-500/10 text-emerald-500' : s === 'heldout' ? 'bg-blue-500/10 text-blue-500' : 'bg-gf-surface2 text-gf-muted';

  // instrumentId 形如 venue:product:symbol → 拆出交易所/产品保身份跳转
  const identityOf = (instrumentId: string) => {
    const [venue, product] = instrumentId.split(':');
    return { venue: venue || 'binance', product: (product || 'spot') as 'spot' | 'perp' };
  };

  return (
    <div className="card p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-gf-text">{zh ? '样本明细' : 'Sample details'}</div>
        {q.data && <span className="text-[11px] text-gf-muted">{zh ? `共 ${total} 条` : `${total} samples`}</span>}
      </div>

      {legacyTruncated && (
        <div className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-500">
          {zh
            ? `此任务由旧版本运行，仅保存前 ${total} 条样本（事件总计 ${report.overview.events}）；重新运行相同配置可获得全量样本。`
            : `Run on an older build: only the first ${total} samples were stored (${report.overview.events} total events); re-run the same config for full samples.`}
        </div>
      )}

      {/* 筛选 */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={symbol}
          onChange={(e) => { setSymbol(e.target.value); setCursor(0); }}
          placeholder={zh ? '币对关键词…' : 'Symbol…'}
          className="input !h-8 w-36 text-xs"
        />
        <select value={split} onChange={(e) => { setSplit(e.target.value as typeof split); setCursor(0); }} className="input !h-8 w-28 text-xs">
          <option value="all">{zh ? '全部' : 'All'}</option>
          <option value="train">{zh ? '训练' : 'Train'}</option>
          <option value="heldout">{zh ? '留出' : 'Held-out'}</option>
          <option value="excluded">{zh ? '剔除' : 'Excluded'}</option>
        </select>
        <label className="flex items-center gap-1 text-xs text-gf-muted">
          <input type="checkbox" checked={invalidOnly} onChange={(e) => { setInvalidOnly(e.target.checked); setCursor(0); }} />
          {zh ? '仅缺失' : 'Invalid only'}
        </label>
      </div>

      {q.isLoading && !q.data ? (
        <div className="py-6 text-center text-xs text-gf-muted">{zh ? '载入中…' : 'Loading…'}</div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-xs text-gf-muted">{zh ? '暂无样本' : 'No samples'}</div>
      ) : (
        <>
          <div className="max-h-[520px] space-y-2 overflow-y-auto">
            {items.map((e) => {
              const idn = identityOf(e.instrumentId);
              const venueName = idn.venue.charAt(0).toUpperCase() + idn.venue.slice(1);
              const productLabel = idn.product === 'spot' ? (zh ? '现货' : 'Spot') : (zh ? '合约' : 'Perp');
              return (
                <div key={`${e.instrumentId}:${e.triggerTime}`} className="rounded-lg border border-gf-border/50 p-2.5 text-xs">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-medium text-gf-text">{e.symbol}</span>
                    <span className="text-[10px] text-gf-muted">{venueName} · {productLabel}</span>
                    <span className={`rounded px-1.5 text-[10px] ${splitTone(e.split)}`}>{splitLabel(e.split)}</span>
                    <span className="text-gf-muted">{fmtTime(e.triggerTime)}</span>
                    <span className="text-gf-muted">p0 = {fmtPriceSmart(e.p0)}</span>
                    <a
                      href={`/coin/${e.symbol}${idn.product === 'spot' ? '?type=spot' : '?type=perp'}`}
                      className="ml-auto text-gf-accent hover:underline"
                    >
                      {zh ? '查看 K 线 →' : 'View chart →'}
                    </a>
                    <a
                      href={`/replay?instrumentId=${encodeURIComponent(e.instrumentId)}&eventTime=${Date.parse(e.triggerTime) || ''}&window=30&interval=5m&symbol=${encodeURIComponent(e.symbol)}`}
                      className="text-gf-accent hover:underline"
                    >
                      {zh ? '触发时点回放 →' : 'Replay at trigger →'}
                    </a>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-gf-muted">
                    {e.outcomes.map((o) => (
                      <span key={o.h} className={o.valid ? 'text-gf-text' : ''}>
                        {o.h}{zh ? '根' : 'bars'}: {o.valid ? fmtPct(o.r) : `✗ ${o.reason ?? ''}`}
                        {o.valid && o.mfe !== null && ` (MFE ${fmtPct(o.mfe)} / MAE ${fmtPct(o.mae)})`}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-gf-border/50 pt-2 text-xs">
            <button
              disabled={cursor === 0}
              onClick={() => setCursor(Math.max(0, cursor - PAGE))}
              className="rounded border border-gf-border px-2.5 py-1 text-gf-muted hover:text-gf-text disabled:opacity-40"
            >
              {zh ? '上一页' : 'Prev'}
            </button>
            <span className="text-gf-muted">
              {cursor + 1}–{Math.min(cursor + PAGE, total)} / {total}
            </span>
            <button
              disabled={!q.data?.nextCursor}
              onClick={() => q.data?.nextCursor && setCursor(Number(q.data.nextCursor))}
              className="rounded border border-gf-border px-2.5 py-1 text-gf-muted hover:text-gf-text disabled:opacity-40"
            >
              {zh ? '下一页' : 'Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function StudiesPage() {
  const router = useRouter();
  const { language, user, authState } = useAppStore();
  const authReady = useRequireAuth('/research/studies', true);
  const zh = language !== 'en';
  const authed = authState === 'authenticated' && !!user;

  const t = useMemo(
    () => ({
      title: zh ? '条件历史验证' : 'Conditional History Study',
      subtitle: zh ? '过去满足条件后发生什么 —— 事件研究，不模拟成交、不构成胜率' : 'What happened after past conditions matched — event study, no simulated fills, not a win-rate',
      needLogin: zh ? '请先登入以使用历史验证' : 'Sign in to run historical studies',
      login: zh ? '前往登入' : 'Go to login',
      config: zh ? '研究配置' : 'Study setup',
      rule: zh ? '规则' : 'Rule',
      noRules: zh ? '暂无已保存规则，请先在筛选器中建立规则' : 'No saved rules yet — create one in the screener first',
      toScreener: zh ? '前往筛选器' : 'Open screener',
      product: zh ? '产品' : 'Product',
      spot: zh ? '现货' : 'Spot',
      perp: zh ? '合约' : 'Perp',
      venue: zh ? '交易所' : 'Venue',
      quote: zh ? '报价币' : 'Quote',
      topN: zh ? '样本数量' : 'Sample size',
      range: zh ? '历史范围 (UTC)' : 'History range (UTC)',
      start: zh ? '开始' : 'Start',
      end: zh ? '结束' : 'End',
      interval: zh ? 'K线周期' : 'Interval',
      horizons: zh ? '观察窗口' : 'Observation windows',
      benchmark: zh ? '基准资产' : 'Benchmark',
      sampling: zh ? '样本模式' : 'Sampling',
      nonOverlap: zh ? '非重叠（默认）' : 'Non-overlapping (default)',
      allSamples: zh ? '全样本（标注相关性）' : 'All samples (correlated)',
      split: zh ? '训练 / 留出分界' : 'Train / held-out split',
      validate: zh ? '预检' : 'Validate',
      validating: zh ? '预检中…' : 'Validating…',
      run: zh ? '运行研究' : 'Run study',
      running: zh ? '已提交' : 'Submitted',
      tasks: zh ? '我的研究任务' : 'My study tasks',
      emptyTasks: zh ? '暂无研究任务' : 'No studies yet',
      emptyResult: zh ? '选择一个任务查看结果' : 'Select a task to view results',
      cancel: zh ? '取消' : 'Cancel',
      cancelling: zh ? '取消中' : 'Cancelling…',
      progress: zh ? '进度' : 'Progress',
      result: zh ? '研究结果' : 'Results',
      samplesTab: zh ? '样本' : 'Samples',
      coverage: zh ? '数据覆盖' : 'Data coverage',
      overview: zh ? '样本概览' : 'Sample overview',
      distribution: zh ? '收益分布' : 'Return distribution',
      windowTable: zh ? '观察窗口表' : 'Observation windows',
      eventsDetail: zh ? '事件明细' : 'Event details',
      showEvents: zh ? '显示事件明细' : 'Show event details',
      hideEvents: zh ? '收起事件明细' : 'Hide event details',
      insufficient: zh ? '有效样本 < 30：标示样本不足，不生成比较排名' : 'Valid samples < 30: marked insufficient, no comparison ranking',
      survivorship: zh ? '存续偏差：样本池为当前存续币池' : 'Survivorship bias: current surviving universe',
      blocks: zh ? '预检阻塞项' : 'Validation blockers',
      noBlock: zh ? '预检通过，可运行' : 'Validation passed — ready to run',
      estimate: zh ? '预算估计' : 'Cost estimate',
      bars: zh ? '根' : 'bars',
      seconds: zh ? '约' : '~',
      sec: 's',
      viewAll: zh ? '查看全部' : 'View all',
      metric: zh ? '指标' : 'Metric',
      valid: zh ? '有效' : 'Valid',
      missing: zh ? '缺失' : 'Missing',
      median: zh ? '中位' : 'Median',
      mean: zh ? '均值' : 'Mean',
      pos: zh ? '上涨' : 'Up',
      neg: zh ? '下跌' : 'Down',
      zero: zh ? '持平' : 'Flat',
      p5: 'P5', p25: 'P25', p50: 'P50', p75: 'P75', p95: 'P95',
      mfe: 'MFE', mae: 'MAE',
      benchDiff: zh ? '相对基准' : 'vs benchmark',
      train: zh ? '训练' : 'Train',
      heldout: zh ? '留出' : 'Held-out',
      note: zh ? '说明' : 'Note',
      window: zh ? '窗口' : 'Window',
      symbol: zh ? '标的' : 'Symbol',
      trigger: zh ? '触发时间' : 'Triggered at',
      p0: 'p0',
      retry: zh ? '重试' : 'Retry',
      failed: zh ? '载入失败' : 'Failed to load',
      load: zh ? '载入中…' : 'Loading…',
    }),
    [zh],
  );

  const [tab, setTab] = useState<'config' | 'result' | 'samples'>('config');
  const [configOpen, setConfigOpen] = useState(true);
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [screenId, setScreenId] = useState('');
  const [product, setProduct] = useState<'spot' | 'perp'>('spot');
  const [venue, setVenue] = useState('binance');
  const [quote, setQuote] = useState('USDT');
  const [topN, setTopN] = useState(20);
  const [startStr, setStartStr] = useState(utcInput(Date.now() - 30 * 86400_000));
  const [endStr, setEndStr] = useState(utcInput(Date.now()));
  const [interval, setInterval] = useState('1h');
  const [horizons, setHorizons] = useState<number[]>([1, 4, 24]);
  const [benchmark, setBenchmark] = useState('BTCUSDT');
  const [samplingMode, setSamplingMode] = useState<'non_overlapping' | 'all'>('non_overlapping');
  const [split, setSplit] = useState(0.7);
  const [validateRes, setValidateRes] = useState<{ ok: boolean; blockers: Array<{ code: string; messageKey: string; fields?: string[]; detail?: string }>; universePreview: { product: string; venue: string; symbols: number }; estimate: { instruments: number; barsPerInstrument: number; totalBars: number; approxSeconds: number; estimateSecondsMin: number; estimateSecondsMax: number }; coverageSample: { symbol: string; actualBars: number; expectedBars: number; ratio: number; gaps: number } | null; preflightId: string; configHash: string; scope: { instruments: Array<{ symbol: string; venue: string; product: string; quote: string }>; selection: string; asOf: string; plannedInstrumentCount: number; expectedBarsPerInstrument: number } } | null>(null);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [idemKey, setIdemKey] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  // 规则列表
  const rulesQ = useQuery({
    queryKey: ['screener-rules'],
    queryFn: () => api.get<RuleRow[]>('/screener-rules'),
    enabled: authed,
  });
  useEffect(() => {
    if (rulesQ.data?.length) {
      setRules(rulesQ.data);
      if (!screenId || !rulesQ.data.some((r) => r.id === screenId)) setScreenId(rulesQ.data[0].id);
    }
  }, [rulesQ.data, screenId]);

  // 任务列表
  const jobsQ = useQuery({
    queryKey: ['studies-list', language],
    queryFn: () => api.get<StudyJob[]>(`/studies?lang=${language}`),
    enabled: authed,
    refetchInterval: (q) => {
      const list = q.state.data;
      return list?.some((j) => RUNNING_STATES.includes(j.state)) ? 5000 : 60_000;
    },
  });

  // 激活任务轮询
  const activeQ = useQuery({
    queryKey: ['study', activeId, language],
    queryFn: () => api.get<StudyJob>(`/studies/${activeId}?lang=${language}`),
    enabled: authed && !!activeId,
    refetchInterval: (q) => {
      const st = q.state.data?.state;
      return st && RUNNING_STATES.includes(st) ? 3000 : false;
    },
  });

  const activeJob: StudyJob | null = activeQ.data ?? jobsQ.data?.find((j) => j.id === activeId) ?? null;

  /** 预检阻塞项中文化：错误码 → 可读标题；指标 id → 中文名。 */
  const blockerTitle = (code: string): string => {
    const map: Record<string, { zh: string; en: string }> = {
      UNSUPPORTED_METRIC: { zh: '指标暂不支持', en: 'Unsupported metric' },
      INSUFFICIENT_HISTORY: { zh: '历史数据不足', en: 'Insufficient history' },
      NO_SYMBOLS: { zh: '样本池为空', en: 'Empty universe' },
      VALIDATE_ERROR: { zh: '预检请求失败', en: 'Validation failed' },
    };
    return (map[code] ?? { zh: code, en: code })[zh ? 'zh' : 'en'];
  };
  const metricLabel = (id: string): string => {
    const map: Record<string, { zh: string; en: string }> = {
      funding_rate: { zh: '资金费率', en: 'Funding rate' },
      open_interest: { zh: '持仓量', en: 'Open interest' },
      quote_volume: { zh: '成交额', en: 'Quote volume' },
      price: { zh: '价格', en: 'Price' },
      liquidation: { zh: '爆仓', en: 'Liquidation' },
    };
    return (map[id] ?? { zh: id, en: id })[zh ? 'zh' : 'en'];
  };

  const buildRequest = (): StudyRequest => ({
    screenId,
    ruleVersion: rules.find((r) => r.id === screenId)?.ruleVersion,
    universe: { product, venue, quote, topN },
    start: parseUtcInput(startStr),
    end: parseUtcInput(endStr),
    interval,
    horizons: horizons.length ? horizons : [1, 4, 24],
    benchmark,
    samplingMode,
    split,
  });

  const runValidate = async () => {
    setValidating(true);
    setValidateRes(null);
    try {
      const res = await api.post<typeof validateRes>('/studies/validate', buildRequest());
      setValidateRes(res);
    } catch {
      setValidateRes({ ok: false, blockers: [{ code: 'VALIDATE_ERROR', messageKey: 'validate_error', detail: '预检请求失败' }], universePreview: { product, venue, symbols: 0 }, estimate: { instruments: 0, barsPerInstrument: 0, totalBars: 0, approxSeconds: 0, estimateSecondsMin: 0, estimateSecondsMax: 0 }, coverageSample: null, preflightId: '', configHash: '', scope: { instruments: [], selection: '', asOf: '', plannedInstrumentCount: 0, expectedBarsPerInstrument: 0 } });
    } finally {
      setValidating(false);
    }
  };

  const submit = async () => {
    if (!validateRes?.ok) return;
    setSubmitting(true);
    const key = crypto.randomUUID();
    setIdemKey(key);
    try {
      const res = await fetch('/api/v1/studies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}`, 'Idempotency-Key': key },
        body: JSON.stringify({ ...buildRequest(), preflightId: validateRes.preflightId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        if (err?.code === 'PREFLIGHT_EXPIRED' || err?.code === 'PREFLIGHT_STALE') {
          setValidateRes(null);
          alert(err.messageKey === 'study_preflight_stale' ? (zh ? '配置已变化，预检失效，请重新预检' : 'Config changed — preflight stale, re-validate') : (zh ? '预检已失效，请重新预检' : 'Preflight expired — re-validate'));
        } else {
          throw new Error(err?.message || `HTTP ${res.status}`);
        }
        return;
      }
      const job = (await res.json()) as StudyJob;
      setActiveId(job.id);
      setTab('result');
      await jobsQ.refetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : (zh ? '提交失败' : 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const doCancel = async (id: string) => {
    try {
      await api.post<StudyJob>(`/studies/${id}/cancel`);
      await jobsQ.refetch();
      if (activeId === id) await activeQ.refetch();
    } catch { /* ignore */ }
  };

  /** D19：导出完整结果 JSON（manifest + 全部样本 + 排除原因），前端直接下载。 */
  const doExport = async (id: string, cfg: StudyRequest) => {
    try {
      const res = await fetch(`/api/v1/studies/${id}/export`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `study-${id.slice(0, 8)}-${cfg.screenId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert(zh ? '导出失败' : 'Export failed');
    }
  };

  const curRule = rules.find((r) => r.id === screenId);
  const report = activeJob?.result ?? null;

  if (!authed) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 pb-24 pt-6">
        <ResearchTabs />
        <div className="card flex flex-col items-center gap-4 py-16 text-center">
          <div className="text-3xl">🔐</div>
          <div className="text-gf-text">{t.needLogin}</div>
          <Link href="/login" className="btn-primary !h-10">{t.login}</Link>
        </div>
      </div>
    );
  }

  const selected = activeJob ?? null;
  const progressPct = selected?.progress && selected.progress.total
    ? Math.min(100, Math.round(((selected.progress.processed ?? 0) / selected.progress.total) * 100))
    : null;

  
  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-24 pt-6">
      <EventPing event="study_run" />
      <ResearchTabs />
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gf-text">{t.title}</h1>
        <p className="mt-1 text-xs text-gf-muted">{t.subtitle}</p>
      </div>

      {/* 手机 tab：配置 / 结果 / 样本 */}
      <div className="mb-4 flex rounded-lg border border-gf-border bg-gf-surface2 p-0.5 lg:hidden">
        {(['config', 'result', 'samples'] as const).map((k) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex-1 rounded-md py-1.5 text-xs font-medium ${tab === k ? 'bg-gf-accent text-white' : 'text-gf-muted'}`}>
            {k === 'config' ? t.config : k === 'result' ? t.result : t.samplesTab}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        {/* ---------- 左：配置摘要（桌面）/ 配置 tab（手机） ---------- */}
        <div className={tab === 'config' ? 'block' : 'hidden lg:block'}>
          {/* D19：桌面配置面板可折叠 —— 结果为主体 */}
          {!configOpen && (
            <button
              onClick={() => setConfigOpen(true)}
              className="card mb-3 hidden w-full items-center justify-center gap-2 p-2 text-xs text-gf-accent lg:flex"
            >
              {zh ? '展开研究配置' : 'Open study setup'}
            </button>
          )}
          <div className={`card sticky top-16 space-y-4 p-4 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto ${configOpen ? '' : 'hidden lg:block'}`}>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-medium text-gf-muted">{t.config as string}</span>
              <button
                onClick={() => setConfigOpen(false)}
                className="text-[11px] text-gf-muted hover:text-gf-text"
              >
                {zh ? '收合' : 'Collapse'}
              </button>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium text-gf-muted">{t.rule}</div>
              {rulesQ.isLoading ? (
                <div className="text-xs text-gf-muted">{t.load}</div>
              ) : rules.length === 0 ? (
                <div className="space-y-2">
                  <div className="text-xs text-gf-muted">{t.noRules}</div>
                  <Link href="/screener" className="btn-outline !h-8 text-xs">{t.toScreener}</Link>
                </div>
              ) : (
                <select value={screenId} onChange={(e) => setScreenId(e.target.value)} className="input !h-10">
                  {rules.map((r) => (
                    <option key={r.id} value={r.id}>{r.name} (v{r.ruleVersion})</option>
                  ))}
                </select>
              )}
              {curRule && <div className="mt-1 text-[11px] text-gf-muted">v{curRule.ruleVersion}</div>}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <label className="space-y-1">
                <span className="text-xs text-gf-muted">{t.product}</span>
                <select value={product} onChange={(e) => setProduct(e.target.value as 'spot' | 'perp')} className="input !h-9 text-xs">
                  <option value="spot">{t.spot}</option>
                  <option value="perp">{t.perp}</option>
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs text-gf-muted">{t.venue}</span>
                <select value={venue} onChange={(e) => setVenue(e.target.value)} className="input !h-9 text-xs">
                  <option value="binance">Binance</option>
                  <option value="okx">OKX</option>
                  <option value="gate">Gate</option>
                  <option value="bitget">Bitget</option>
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs text-gf-muted">{t.quote}</span>
                <input value={quote} onChange={(e) => setQuote(e.target.value.toUpperCase())} className="input !h-9 text-xs" />
              </label>
              <label className="space-y-1">
                <span className="text-xs text-gf-muted">{t.topN}</span>
                <input type="number" min={1} max={50} value={topN} onChange={(e) => setTopN(Number(e.target.value))} className="input !h-9 text-xs" />
              </label>
            </div>

            <div>
              <div className="mb-1 text-xs font-medium text-gf-muted">{t.range}</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="space-y-1">
                  <span className="text-[11px] text-gf-muted">{t.start}</span>
                  <input type="datetime-local" value={startStr} onChange={(e) => setStartStr(e.target.value)} className="input !h-9 text-xs" />
                </label>
                <label className="space-y-1">
                  <span className="text-[11px] text-gf-muted">{t.end}</span>
                  <input type="datetime-local" value={endStr} onChange={(e) => setEndStr(e.target.value)} className="input !h-9 text-xs" />
                </label>
              </div>
            </div>

            <label className="space-y-1 block">
              <span className="text-xs text-gf-muted">{t.interval}</span>
              <select value={interval} onChange={(e) => setInterval(e.target.value)} className="input !h-9 text-xs">
                {INTERVALS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </label>

            <div>
              <div className="mb-1 text-xs font-medium text-gf-muted">{t.horizons}</div>
              <div className="flex flex-wrap gap-1.5">
                {HORIZON_OPTIONS.map((h) => (
                  <button key={h} onClick={() => setHorizons((prev) => prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h])}
                    className={`rounded-md border px-2.5 py-1 text-xs ${horizons.includes(h) ? 'border-gf-accent bg-gf-accent/10 text-gf-accent' : 'border-gf-border text-gf-muted'}`}>
                    {h} {zh ? '根' : 'bars'}
                  </button>
                ))}
              </div>
            </div>

            <label className="space-y-1 block">
              <span className="text-xs text-gf-muted">{t.benchmark}</span>
              <input value={benchmark} onChange={(e) => setBenchmark(e.target.value.toUpperCase())} className="input !h-9 text-xs" />
            </label>

            <div>
              <div className="mb-1 text-xs font-medium text-gf-muted">{t.sampling}</div>
              <div className="space-y-1">
                <label className="flex items-center gap-1.5 text-xs text-gf-text">
                  <input type="radio" checked={samplingMode === 'non_overlapping'} onChange={() => setSamplingMode('non_overlapping')} />
                  {t.nonOverlap}
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gf-text">
                  <input type="radio" checked={samplingMode === 'all'} onChange={() => setSamplingMode('all')} />
                  {t.allSamples}
                </label>
              </div>
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between text-xs text-gf-muted">
                <span>{t.split}</span>
                <span className="text-gf-text">{Math.round(split * 100)}% / {Math.round((1 - split) * 100)}%</span>
              </div>
              <input type="range" min={0.5} max={0.9} step={0.05} value={split} onChange={(e) => setSplit(Number(e.target.value))} className="w-full" />
            </div>

            <div className="space-y-2 pt-1">
              <button onClick={runValidate} disabled={!screenId || validating} className="btn-outline w-full !h-10">
                {validating ? t.validating : t.validate}
              </button>
              <button onClick={submit} disabled={!validateRes?.ok || submitting} className="btn-primary w-full !h-10">
                {submitting ? t.running : t.run}
              </button>
            </div>

            {validateRes && (
              <div className={`rounded-lg border p-3 text-xs ${validateRes.ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                {validateRes.ok ? (
                  <div className="space-y-1 text-gf-text">
                    <div>✓ {t.noBlock}</div>
                    <div className="text-gf-muted">{t.estimate}: {validateRes.estimate.instruments} 标的 × {validateRes.estimate.barsPerInstrument} {t.bars} = {validateRes.estimate.totalBars.toLocaleString()} {t.bars}</div>
                    <div className="text-gf-muted">{t.seconds}{validateRes.estimate.approxSeconds}s（范围 {validateRes.estimate.estimateSecondsMin}–{validateRes.estimate.estimateSecondsMax}s）</div>
                    {validateRes.coverageSample && (
                      <div className="text-gf-muted">{validateRes.coverageSample.symbol}: {validateRes.coverageSample.actualBars}/{validateRes.coverageSample.expectedBars} ({Math.round(validateRes.coverageSample.ratio * 100)}%)</div>
                    )}
                    <div className="text-gf-muted">scope: {validateRes.scope.plannedInstrumentCount} {zh ? '标的' : 'symbols'} · {validateRes.scope.selection} · {zh ? '已锁定（运行不再重选）' : 'locked (no re-selection at run time)'}</div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="font-medium text-red-500">{t.blocks}</div>
                    {validateRes.blockers.map((b, i) => (
                      <div key={i} className="text-gf-muted">
                        · <span className="text-gf-text">{blockerTitle(b.code)}</span>
                        {b.fields?.length ? `：${b.fields.map(metricLabel).join('、')}` : ''}
                        {b.detail ? ` — ${fmtStudyReason(b.detail, zh)}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ---------- 右：任务列表 + 结果 ---------- */}
        <div className={tab === 'result' || tab === 'samples' ? 'block' : 'hidden lg:block'}>
          {/* 任务列表 */}
          <div className="card mb-4 p-4">
            <div className="mb-2 text-sm font-medium text-gf-text">{t.tasks}</div>
            {jobsQ.isLoading ? (
              <div className="text-xs text-gf-muted">{t.load}</div>
            ) : !jobsQ.data?.length ? (
              <div className="text-xs text-gf-muted">{t.emptyTasks}</div>
            ) : (
              <div className="space-y-2">
                {jobsQ.data.map((j) => {
                  const st = STATE_LABEL[j.state] ?? { zh: j.state, en: j.state, color: 'text-gf-muted' };
                  const p = j.progress && j.progress.total ? Math.min(100, Math.round(((j.progress.processed ?? 0) / j.progress.total) * 100)) : null;
                  return (
                    <div key={j.id}
                      onClick={() => { setActiveId(j.id); setTab('result'); }}
                      className={`cursor-pointer rounded-lg border p-3 ${activeId === j.id ? 'border-gf-accent bg-gf-accent/5' : 'border-gf-border'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2 text-xs">
                          <span className="truncate text-gf-text">{curRule?.name ?? j.config.screenId}</span>
                          <span className={`shrink-0 font-medium ${st.color}`}>{zh ? st.zh : st.en}</span>
                        </div>
                        <span className="shrink-0 text-[11px] text-gf-muted">{fmtTime(j.createdAt)}</span>
                      </div>
                      {p !== null && RUNNING_STATES.includes(j.state) && (
                        <div className="mt-2 h-1.5 overflow-hidden rounded bg-gf-surface2">
                          <div className="h-full rounded bg-gf-accent" style={{ width: `${p}%` }} />
                        </div>
                      )}
                      {p !== null && <div className="mt-1 text-[11px] text-gf-muted">{t.progress}: {j.progress?.processed}/{j.progress?.total}{j.progress?.valid !== undefined ? ` · ${zh ? '有效' : 'valid'} ${j.progress.valid} · ${zh ? '跳过' : 'skipped'} ${j.progress.skipped ?? 0} · ${zh ? '失败' : 'failed'} ${j.progress.failed ?? 0}` : ''}</div>}
                      {j.state === 'partial' && j.result?.partialReasons?.length ? (
                        <div className="mt-1 text-[11px] text-amber-500">{j.result.partialReasons.map((r) => fmtStudyReason(r, zh)).join('；')}</div>
                      ) : null}
                      {RUNNING_STATES.includes(j.state) && (
                        <button onClick={(e) => { e.stopPropagation(); doCancel(j.id); }} className="mt-2 text-[11px] text-red-500 hover:underline">
                          {zh ? t.cancel : 'Cancel'}
                        </button>
                      )}
                      {!RUNNING_STATES.includes(j.state) && j.result && (
                        <button
                          onClick={(e) => { e.stopPropagation(); doExport(j.id, j.config); }}
                          className="mt-2 text-[11px] text-gf-accent hover:underline"
                        >
                          {zh ? '导出结果 (JSON)' : 'Export JSON'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 结果区 */}
          {!selected ? (
            <div className="card flex flex-col items-center gap-3 py-16 text-center">
              <div className="text-3xl">📊</div>
              <div className="text-gf-muted">{t.emptyResult}</div>
            </div>
          ) : !report ? (
            <div className="card flex flex-col items-center gap-3 py-16 text-center">
              <div className="text-2xl">{STATE_LABEL[selected.state]?.zh ?? selected.state}</div>
              {progressPct !== null && (
                <div className="w-56">
                  <div className="mb-1 text-xs text-gf-muted">{t.progress}: {selected.progress?.processed}/{selected.progress?.total}{selected.progress?.valid !== undefined ? ` · ${zh ? '有效' : 'valid'} ${selected.progress.valid} · ${zh ? '跳过' : 'skipped'} ${selected.progress.skipped ?? 0} · ${zh ? '失败' : 'failed'} ${selected.progress.failed ?? 0}` : ''}</div>
                  <div className="h-2 overflow-hidden rounded bg-gf-surface2">
                    <div className="h-full rounded bg-gf-accent" style={{ width: `${progressPct}%` }} />
                  </div>
                </div>
              )}
              {selected.error && <div className="text-xs text-red-500">{selected.error.message ?? selected.error.code}</div>}
            </div>
          ) : (
            <ReportView report={report} t={t} tab={tab} zh={zh} activeId={activeId ?? selected.id} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ 结果视图 ------------------------------ */

function ReportView({ report, t, tab, zh, activeId }: {
  report: StudyReport;
  t: Record<string, string | ((...args: never[]) => string)>;
  tab: 'config' | 'result' | 'samples';
  zh: boolean;
  activeId: string;
}) {
  const horizons = report.horizons;
  // D13: 手机端观察窗口明细表默认折叠，先显示摘要卡
  const [windowDetailOpen, setWindowDetailOpen] = useState(false);

  const CoverageCard = (
    <div className="card p-4">
      <div className="mb-2 text-sm font-medium text-gf-text">{t.coverage as string}</div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-lg font-semibold text-gf-text">{report.coverage.instrumentsWithData}/{report.coverage.instrumentsTried}</div>
          <div className="text-[11px] text-gf-muted">{zh ? '有效标的' : 'instruments'}</div>
        </div>
        <div>
          <div className="text-lg font-semibold text-gf-text">{Math.round(report.coverage.avgRatio * 100)}%</div>
          <div className="text-[11px] text-gf-muted">{zh ? '平均覆盖' : 'coverage'}</div>
        </div>
        <div>
          <div className="text-lg font-semibold text-gf-text">{report.coverage.gapsTotal}</div>
          <div className="text-[11px] text-gf-muted">{zh ? '缺口' : 'gaps'}</div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="rounded bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-500">{t.survivorship as string}</span>
        {report.insufficient && <span className="rounded bg-red-500/10 px-2 py-0.5 text-[11px] text-red-500">{t.insufficient as string}</span>}
      </div>
    </div>
  );

  const OverviewCard = (
    <div className="card p-4">
      <div className="mb-2 text-sm font-medium text-gf-text">{t.overview as string}</div>
      <div className="grid grid-cols-3 gap-2 text-center sm:grid-cols-6">
        {[
          [report.overview.events, zh ? '事件' : 'events'],
          [report.overview.instruments, zh ? '标的' : 'instruments'],
          [report.overview.suppressedTotal, zh ? '抑制' : 'suppressed'],
          [report.overview.train, t.train as string],
          [report.overview.heldout, t.heldout as string],
          [report.overview.excluded, zh ? '跨界剔除' : 'excluded'],
        ].map(([v, label], i) => (
          <div key={i}>
            <div className="text-lg font-semibold text-gf-text">{String(v)}</div>
            <div className="text-[11px] text-gf-muted">{label}</div>
          </div>
        ))}
      </div>
      {/* D19：每个分母的口径解释，不再把「6484」这类含义藏在标签里 */}
      <div className="mt-3 space-y-1 border-t border-gf-border/50 pt-2 text-[11px] text-gf-muted">
        <div>· {zh ? '事件：按唯一键（任务＋标的＋规则版本＋触发时点）去重后的有效样本数' : 'Events: deduplicated by task+instrument+ruleVersion+trigger time'}</div>
        <div>· {zh ? '标的：样本池中有数据且至少触发一次事件的标的数' : 'Instruments: instruments with data and ≥1 trigger'}</div>
        <div>· {zh ? '抑制：非重叠模式下，处于前一事件最大观察窗内而被跳过的次生触发' : 'Suppressed: secondary triggers skipped inside a prior observation window (non-overlapping mode)'}</div>
        <div>· {zh ? '训练 / 留出：按「开始＋(结束−开始)×分界比例」的时间点切分' : 'Train / held-out: split by start + (end−start)×split ratio'}</div>
        <div>· {zh ? '跨界剔除：观察窗口跨过训练/留出分界的样本，不纳入任何一方统计' : 'Excluded: observation window straddles the train/held-out boundary'}</div>
      </div>
    </div>
  );

  const WindowTable = (
    <div className="card overflow-x-auto p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium text-gf-text">{t.windowTable as string}</div>
        <button
          type="button"
          onClick={() => setWindowDetailOpen(!windowDetailOpen)}
          className="rounded border border-gf-border bg-gf-surface2 px-2.5 py-1 text-xs text-gf-accent sm:hidden"
        >
          {windowDetailOpen ? (zh ? '收起明细' : 'Collapse') : (zh ? '展开明细' : 'Expand')}
        </button>
      </div>

      {/* D13: 手机窗口摘要卡 —— 窗口 × 有效样本 × 中位值，展开后再看横向明细表 */}
      <div className="flex flex-col gap-1.5 sm:hidden">
        {horizons.map((h) => (
          <div key={h.h} className="flex items-center justify-between gap-2 rounded-md border border-gf-border/60 bg-gf-surface2/50 px-2.5 py-2 text-xs">
            <span className="font-medium text-gf-text">
              {h.h} {zh ? '根' : 'bars'} <span className="text-gf-muted">({labelOfH(h.h, report.config.interval, zh)})</span>
            </span>
            <span className="text-gf-muted">
              n={h.valid}
              {h.missing ? ` · 缺 ${h.missing}` : ''}
              {h.insufficient ? ` · ${zh ? '样本不足' : 'insufficient'}` : ''}
            </span>
            <span className={`font-mono ${h.insufficient ? 'text-red-500' : 'text-gf-text'}`}>{fmtPct(h.median)}</span>
          </div>
        ))}
      </div>

      <div className={windowDetailOpen ? 'mt-3' : 'mt-3 hidden sm:block'}>
        <table className="w-full min-w-[640px] text-xs">
          <thead>
            <tr className="border-b border-gf-border text-gf-muted">
              <th className="sticky left-0 z-10 bg-gf-surface py-2 text-left font-medium">{t.metric as string}</th>
              <th className="py-2 text-right font-medium">{t.valid as string}</th>
              <th className="py-2 text-right font-medium">{t.missing as string}</th>
              <th className="py-2 text-right font-medium">{t.median as string}</th>
              <th className="py-2 text-right font-medium">{t.mean as string}</th>
              <th className="py-2 text-right font-medium">{t.pos as string}</th>
              <th className="py-2 text-right font-medium">{t.neg as string}</th>
              <th className="py-2 text-right font-medium">{t.zero as string}</th>
              <th className="py-2 text-right font-medium">P25</th>
              <th className="py-2 text-right font-medium">P75</th>
              <th className="py-2 text-right font-medium">P95</th>
              <th className="py-2 text-right font-medium">{t.mfe as string}</th>
              <th className="py-2 text-right font-medium">{t.mae as string}</th>
              <th className="py-2 text-right font-medium">{t.benchDiff as string}</th>
            </tr>
          </thead>
          <tbody>
            {horizons.map((h) => (
              <tr key={h.h} className="border-b border-gf-border/50 text-gf-text">
                <td className="sticky left-0 z-10 bg-gf-surface py-2">
                  <span className="font-medium">{h.h} {zh ? '根' : 'bars'}</span>
                  <span className="ml-1 text-gf-muted">({labelOfH(h.h, report.config.interval, zh)})</span>
                  {h.insufficient && <span className="ml-1 rounded bg-red-500/10 px-1 text-[10px] text-red-500">{zh ? '样本不足' : 'insufficient'}</span>}
                </td>
                <td className="py-2 text-right">{h.valid}</td>
                <td className="py-2 text-right text-gf-muted">{h.missing}</td>
                <td className="py-2 text-right">{fmtPct(h.median)}</td>
                <td className="py-2 text-right">{fmtPct(h.mean)}</td>
                <td className="py-2 text-right text-emerald-500">{h.posPct !== null ? `${h.posPct}%` : '—'}</td>
                <td className="py-2 text-right text-red-500">{h.negPct !== null ? `${h.negPct}%` : '—'}</td>
                <td className="py-2 text-right text-gf-muted">{h.zeroPct !== null ? `${h.zeroPct}%` : '—'}</td>
                <td className="py-2 text-right">{fmtPct(h.p25)}</td>
                <td className="py-2 text-right">{fmtPct(h.p75)}</td>
                <td className="py-2 text-right">{fmtPct(h.p95)}</td>
                <td className="py-2 text-right">{fmtPct(h.mfeMedian)}</td>
                <td className="py-2 text-right">{fmtPct(h.maeMedian)}</td>
                <td className="py-2 text-right">{fmtPP(report.benchmark?.diff?.[String(h.h)] ?? null)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const DistributionCard = (
    <div className="card p-4">
      <div className="mb-2 text-sm font-medium text-gf-text">{t.distribution as string}</div>
      <div className="space-y-3">
        {horizons.map((h) => (
          <div key={h.h}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-gf-text">{h.h} {zh ? '根' : 'bars'} ({labelOfH(h.h, report.config.interval, zh)})</span>
              <span className="text-gf-muted">n={h.valid}{h.insufficient ? ` · ${zh ? '样本不足' : 'insufficient'}` : ''}</span>
            </div>
            <div className="flex h-8 overflow-hidden rounded-lg text-[10px] text-white">
              <div className="flex items-center justify-center bg-emerald-500" style={{ width: `${h.posPct ?? 0}%` }}>{h.posPct ?? 0}%</div>
              <div className="flex items-center justify-center bg-gf-surface2 text-gf-muted" style={{ width: `${h.zeroPct ?? 0}%` }}>{h.zeroPct ?? 0}%</div>
              <div className="flex items-center justify-center bg-red-500" style={{ width: `${h.negPct ?? 0}%` }}>{h.negPct ?? 0}%</div>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-gf-muted">
              <span>P5 {fmtPct(h.p5)}</span><span>P25 {fmtPct(h.p25)}</span>
              <span className="text-gf-text">{t.median as string} {fmtPct(h.median)}</span>
              <span>P75 {fmtPct(h.p75)}</span><span>P95 {fmtPct(h.p95)}</span>
              <span>{t.mfe as string} {fmtPct(h.mfeMedian)}</span>
              <span>{t.mae as string} {fmtPct(h.maeMedian)}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 border-t border-gf-border/50 pt-2 text-[11px] text-gf-muted">
        {report.basis}
      </div>
    </div>
  );

  const SplitCard = (
    <div className="card p-4">
      <div className="mb-2 text-sm font-medium text-gf-text">{zh ? '训练 / 留出' : 'Train / Held-out'}</div>
      {(['train', 'heldout'] as const).map((part) => {
        const p = report.split[part];
        return (
          <div key={part} className="mb-2">
            <div className="mb-1 text-xs font-medium text-gf-text">{part === 'train' ? t.train as string : t.heldout as string} · {splitNoteOf(p.note, zh, report.config.split)}</div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] text-[11px]">
                <thead>
                  <tr className="border-b border-gf-border text-gf-muted">
                    <th className="py-1 text-left font-medium">{t.window as string}</th>
                    <th className="py-1 text-right font-medium">n</th>
                    <th className="py-1 text-right font-medium">{t.median as string}</th>
                    <th className="py-1 text-right font-medium">{t.mean as string}</th>
                    <th className="py-1 text-right font-medium">{t.mfe as string}</th>
                    <th className="py-1 text-right font-medium">{t.mae as string}</th>
                  </tr>
                </thead>
                <tbody>
                  {p.horizons.map((h) => (
                    <tr key={h.h} className="border-b border-gf-border/40 text-gf-text">
                      <td className="py-1">{h.h} {zh ? '根' : 'bars'}{h.insufficient ? ' ⚠️' : ''}</td>
                      <td className="py-1 text-right">{h.valid}</td>
                      <td className="py-1 text-right">{fmtPct(h.median)}</td>
                      <td className="py-1 text-right">{fmtPct(h.mean)}</td>
                      <td className="py-1 text-right">{fmtPct(h.mfeMedian)}</td>
                      <td className="py-1 text-right">{fmtPct(h.maeMedian)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );

  const EventsBlock = (
    <PaginatedEvents taskId={activeId} zh={zh} report={report} />
  );

  // 手机：结果 tab 显示 覆盖率+概览+窗口表+分布；样本 tab 显示 明细+训练/留出
  const resultMode = tab === 'samples' ? 'samples' : 'result';
  const counts = report.counts;
  const D02CountsCard = counts ? (
    <div className="card p-4">
      <div className="mb-2 text-sm font-medium text-gf-text">{zh ? '任务计数（D02 固定范围）' : 'Task counts (fixed scope)'}</div>
      <div className="grid grid-cols-3 gap-2 text-center sm:grid-cols-6">
        {[
          [counts.plannedInstrumentCount, zh ? '计划' : 'planned'],
          [counts.processedInstrumentCount, zh ? '已处理' : 'processed'],
          [counts.validInstrumentCount, zh ? '有效' : 'valid'],
          [counts.skippedInstrumentCount, zh ? '跳过' : 'skipped'],
          [counts.failedInstrumentCount, zh ? '失败' : 'failed'],
          [counts.eventCount, zh ? '事件' : 'events'],
        ].map(([v, label], i) => (
          <div key={i}>
            <div className="text-lg font-semibold text-gf-text">{String(v)}</div>
            <div className="text-[11px] text-gf-muted">{label}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-gf-muted">
        {zh ? `K线 ${counts.barCount.toLocaleString()} 根 · 耗时 ${report.elapsedMs ? `${(report.elapsedMs / 1000).toFixed(1)}s` : '—'} · scope ${report.preflight?.selection ?? '—'}${report.preflight ? ` · ${report.preflight.asOf.slice(0, 16)}Z 锁定` : ''}` : `${counts.barCount.toLocaleString()} bars · ${report.elapsedMs ? `${(report.elapsedMs / 1000).toFixed(1)}s elapsed` : '—'} · scope ${report.preflight?.selection ?? '—'}`}
      </div>
    </div>
  ) : null;
  return (
    <div className="space-y-4">
      {report.partialReasons?.length ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-500">
          <div className="font-medium">{zh ? '部分完成：' : 'Partial: '}{report.partialReasons.map((r) => fmtStudyReason(r, zh)).join('；')}</div>
          <div className="mt-0.5 text-[11px] opacity-80">{zh ? '覆盖不足不会以绿色「完成」隐藏，结果仅反映有效标的。' : 'Incomplete coverage is not hidden behind a green "succeeded"; results reflect valid instruments only.'}</div>
        </div>
      ) : null}
      {D02CountsCard}
      {tab === 'result' && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {CoverageCard}
            {OverviewCard}
          </div>
          {DistributionCard}
          {WindowTable}
          {SplitCard}
        </>
      )}
      {tab === 'samples' && (
        <>
          {CoverageCard}
          {OverviewCard}
          {SplitCard}
          {EventsBlock}
        </>
      )}
      {resultMode === 'result' && (
        <div className="text-[11px] text-gf-muted">
          {zh ? '留出区间结果不得自动用于调参；跨资产样本不假设独立；比较排名仅在全部窗口有效样本 ≥ 30 时可用。' : 'Held-out results must not be used for tuning; cross-asset samples are not assumed independent; comparison ranking only when every window has ≥ 30 valid samples.'}
        </div>
      )}
    </div>
  );
}
